import React, { useRef, useReducer } from 'react'
import PropTypes from 'prop-types'

import styled from '@emotion/styled'

import detectMobileBrowser from '../../../../utils/detectMobileBrowser'
import { DIRECTION_LEFT, DIRECTION_RIGHT } from '../../../../helpers/const'
import Arrow from '../../Generics/Arrow'

const StyledRoot = styled.div(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
}))

const StyledContainer = styled.div(() => ({
  width: '100%',
  position: 'relative',
  display: 'flex',
}))

const ListWrapper = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  flex: '1 1 auto',
}))

const ListContainer = styled.div(({ scrollable, translateX }) => ({
  display: 'flex',
  ...(scrollable ? {
    overflowY: 'hidden',
    overflowX: 'scroll',
    WebkitOverflowScrolling: 'touch',
    // @see https://stackoverflow.com/a/38994837/9293528
    msOverflowStyle: 'none', // IE 10+
    scrollbarWidth: 'none', // Firefox
    '&::-webkit-scrollbar': {
      display: 'none', // Safari and Chrome
    },
  } : {
    transition: '.25s transform',
    transform: `translate3d(${translateX}px, 0px, 0px)`,
  }),
}))

const ScrollOverlay = styled.div(({ direction, show }) => ({
  display: show ? 'block' : 'none',
  backgroundImage: direction === DIRECTION_LEFT
    ? 'linear-gradient(45deg, #fffc, hsla(0,0%,100%,0) 80%)'
    : 'linear-gradient(90deg, hsla(0,0%,100%,0), #fffc 80%)',
  height: '100%',
  pointerEvents: 'none',
  position: 'absolute',
  right: direction === DIRECTION_RIGHT ? '32px' : undefined,
  left: direction === DIRECTION_LEFT ? '32px' : undefined,
  top: '0',
  width: '80px',
  zIndex: '1',
}))

const HorizontalListScroller = ({
  children,
  scrollWidth,
}) => {
  const isDesktopBrowser = !detectMobileBrowser()
  const [{
    translateX,
    isRightLimit,
    isLeftLimit,
  }, dispatch] = useReducer((state, newState) => ({ ...state, ...newState }), {
    translateX: 0,
    isRightLimit: false,
    isLeftLimit: true,
  })
  const listWrapperRef = useRef(null)
  const listContainerRef = useRef(null)

  React.useEffect(() => {
    const { current: scrollbarWrapper } = listWrapperRef
    const { current: scrollbar } = listContainerRef
    if (scrollbarWrapper && scrollbar) {
      const isLeftLimitReached = translateX + scrollWidth >= 0
      const isRightLimitReached = (
        Math.abs(translateX - scrollWidth - scrollbarWrapper.clientWidth) >= scrollbar.clientWidth
      )

      dispatch({
        isLeftLimit: isLeftLimitReached,
        isRightLimit: isRightLimitReached,
        translateX: 0,
      })
    }
  }, [children])

  const onArrowLeftClick = () => {
    const { current: scrollbarWrapper } = listWrapperRef
    const { current: scrollbar } = listContainerRef
    if (scrollbarWrapper && scrollbar) {
      const translate = translateX + scrollWidth
      const isLimitReached = translate >= 0

      dispatch({
        isRightLimit: false,
        isLeftLimit: isLimitReached,
        translateX: isLimitReached ? 0 : translate,
      })
    }
  }

  const onArrowRightClick = () => {
    const { current: scrollbarWrapper } = listWrapperRef
    const { current: scrollbar } = listContainerRef
    if (scrollbarWrapper && scrollbar) {
      const translate = translateX - scrollWidth
      const isLimitReached = (
        Math.abs(translate - scrollbarWrapper.clientWidth) >= scrollbar.clientWidth
      )

      dispatch({
        isLeftLimit: false,
        isRightLimit: isLimitReached,
        translateX: (
          isLimitReached
            ? -scrollbar.clientWidth + scrollbarWrapper.clientWidth
            : translate
        ),
      })
    }
  }

  return (
    <StyledRoot data-test="HorizontalListScroller">
      <StyledContainer>
        <ScrollOverlay direction={DIRECTION_LEFT} show={!isLeftLimit} />

        {isDesktopBrowser && !isLeftLimit ? (
          <Arrow
            active={!isLeftLimit}
            onClick={onArrowLeftClick}
            direction={DIRECTION_LEFT}
          />
        ) : null}
        <ListWrapper
          ref={listWrapperRef}
          scrollable={!isDesktopBrowser}
        >
          <ListContainer
            ref={listContainerRef}
            scrollable={!isDesktopBrowser}
            translateX={translateX}
          >
            {children}
          </ListContainer>
        </ListWrapper>

        {isDesktopBrowser && !isRightLimit ? (
          <Arrow
            active={!isRightLimit}
            onClick={onArrowRightClick}
            direction={DIRECTION_RIGHT}
          />
        ) : null}
        <ScrollOverlay direction={DIRECTION_RIGHT} show={!isRightLimit} />
      </StyledContainer>
    </StyledRoot>
  )
}

HorizontalListScroller.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({})),
    PropTypes.shape({}),
  ]).isRequired,
  scrollWidth: PropTypes.number,
}

HorizontalListScroller.defaultProps = {
  scrollWidth: 300,
}

HorizontalListScroller.displayName = 'HorizontalListScroller'
HorizontalListScroller.whyDidYouRender = true

export default React.memo(HorizontalListScroller)
